// src/routes/domains/quantum/quantum.routes.tsx

import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { createProtectedRoute } from '../../core/createProtectedRoute';
import type { AppRoute } from '../../core/types';

// ------------------------------
// Lazy‑loaded Quantum components (absolute imports)
// ------------------------------

// Core quantum systems
const AppealOSDashboard = lazy(() => import('@/pages/appeal/AppealOSDashboard'));
const AppealLetterPage = lazy(() => import('@/pages/appeal/AppealLetterPage'));
const AppealDeadlinesPage = lazy(() => import('@/pages/appeal/AppealDeadlinesPage'));
const AppealPathwayPage = lazy(() => import('@/pages/appeal/AppealPathwayPage'));
const AppealEvidencePage = lazy(() => import('@/pages/appeal/AppealEvidencePage'));
const AppealJrPage = lazy(() => import('@/pages/appeal/AppealJrPage'));
const AppealGuidancePage = lazy(() => import('@/pages/appeal/AppealGuidancePage'));
const AppealChatPage = lazy(() => import('@/pages/appeal/AppealChatPage'));
const AppealDefenceCompanion = lazy(() => import('@/pages/appeal/AppealDefenceCompanion'));
const QuantumLeapSuperSystem = lazy(() => import('@/components/quantum/QuantumLeapSuperSystem'));
const QuantumUnificationEngine = lazy(() => import('@/components/quantum/QuantumUnificationEngine'));
const QuantumLeapWithNav = lazy(() => import('@/pages/quantum/QuantumLeapWithNav'));
const QuantumControlCenter = lazy(() => import('@/components/settings/QuantumControlCenter'));
// EmbassyQuantumPulse import removed 2026-08-16: component archived earlier this session
// to archive/embassy-fabrication/ (100% fabricated "live" data, fake 87% strike prediction).
// This lazy import was left dangling here and broke the vite build once this file was
// touched again. Route below now redirects.

// Interdimensional / Multiverse
const InterdimensionalFramework = lazy(() => import('@/components/interdimensional/InterdimensionalFramework'));
const ParallelUniversePage = lazy(() => import('@/pages/interdimensional/ParallelUniversePage'));
const MultiverseDashboardPage = lazy(() => import('@/pages/interdimensional/MultiverseDashboardPage'));
const RealityBranchExplorer = lazy(() => import('@/components/interdimensional/RealityBranchExplorer'));

// Cosmic & Temporal
const CosmicImmigrationConsciousness = lazy(() => import('@/components/cosmic/CosmicImmigrationConsciousness'));
const TemporalParadoxResolutionEngine = lazy(() => import('@/components/temporal/TemporalParadoxResolutionEngine'));
const OmegaConvergenceEngine = lazy(() => import('@/components/omega/OmegaConvergenceEngine'));

// Quantum intelligence & predictions
const QuantumPredictions = lazy(() => import('@/components/QuantumPredictions'));
const QuantumDependencyCommander = lazy(() => import('@/components/QuantumDependencyCommander'));

// Other advanced systems
const QuantumOrchestrator = lazy(() => import('@/systems/orchestration/QuantumOrchestrator'));
const ToolSynergyMatrix = lazy(() => import('@/systems/synergy/ToolSynergyMatrix'));
const AutonomousWorkflowOrchestrator = lazy(() => import('@/systems/synergy/AutonomousWorkflowOrchestrator'));
// DocumentIntelligenceBridge import removed 2026-08-16: component archived, see
// archive/document-intelligence-bridge-fabrication/ — 100% hardcoded fake "document flow"
// data (fabricated confidence scores, "High success probability detected" outcome
// predictions), no real system behind it. Route below now redirects.
const SuccessPatternAI = lazy(() => import('@/systems/autonomy/SuccessPatternAI'));
const EmotionalIntelligenceEngine = lazy(() => import('@/systems/autonomy/EmotionalIntelligenceEngine'));
const PredictiveOptimizer = lazy(() => import('@/systems/autonomy/PredictiveOptimizer'));

// ------------------------------
// Quantum Routes Definition
// ------------------------------
export const quantumRoutes: AppRoute[] = [
  // AppealOS rebuild (2026-09): Commander + old Quantum shell → hub.
  {
    id: 'quantum.commander',
    domain: 'quantum',
    path: '/commander',
    element: <Navigate to="/quantum-appeal" replace />,
  },
  {
    id: 'quantum.appeal_orchestrator',
    domain: 'quantum',
    path: '/quantum-appeal',
    element: createProtectedRoute(<AppealOSDashboard />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'AppealOS',
        region: 'Multiverse',
        intent: 'dashboard',
        aiTags: ['appeal', 'appealos'],
        toolId: 'quantum-appeal',
        category: 'integration',
      },
    }),
  },
  {
    id: 'quantum.appeal_letter',
    domain: 'quantum',
    path: '/quantum-appeal/letter',
    element: createProtectedRoute(<AppealLetterPage />, {
      tier: 'premium_pro',
      meta: { title: 'AppealOS · Letter', toolId: 'quantum-appeal', category: 'integration' },
    }),
  },
  {
    id: 'quantum.appeal_deadlines',
    domain: 'quantum',
    path: '/quantum-appeal/deadlines',
    element: createProtectedRoute(<AppealDeadlinesPage />, {
      tier: 'premium_pro',
      meta: { title: 'AppealOS · Deadlines', toolId: 'quantum-appeal', category: 'integration' },
    }),
  },
  {
    id: 'quantum.appeal_pathway',
    domain: 'quantum',
    path: '/quantum-appeal/pathway',
    element: createProtectedRoute(<AppealPathwayPage />, {
      tier: 'premium_pro',
      meta: { title: 'AppealOS · Pathway', toolId: 'quantum-appeal', category: 'integration' },
    }),
  },
  {
    id: 'quantum.appeal_evidence',
    domain: 'quantum',
    path: '/quantum-appeal/evidence',
    element: createProtectedRoute(<AppealEvidencePage />, {
      tier: 'premium_pro',
      meta: { title: 'AppealOS · Evidence', toolId: 'quantum-appeal', category: 'integration' },
    }),
  },
  {
    id: 'quantum.appeal_jr',
    domain: 'quantum',
    path: '/quantum-appeal/jr',
    element: createProtectedRoute(<AppealJrPage />, {
      tier: 'premium_pro',
      meta: { title: 'AppealOS · JR', toolId: 'quantum-appeal', category: 'integration' },
    }),
  },
  {
    id: 'quantum.appeal_guidance',
    domain: 'quantum',
    path: '/quantum-appeal/guidance',
    element: createProtectedRoute(<AppealGuidancePage />, {
      tier: 'premium_pro',
      meta: { title: 'AppealOS · Guidance', toolId: 'quantum-appeal', category: 'integration' },
    }),
  },
  {
    id: 'quantum.appeal_chat',
    domain: 'quantum',
    path: '/quantum-appeal/chat',
    element: createProtectedRoute(<AppealChatPage />, {
      tier: 'premium_pro',
      meta: { title: 'AppealOS · Chat', toolId: 'quantum-appeal', category: 'integration' },
    }),
  },
  {
    id: 'quantum.appeal_defence',
    domain: 'quantum',
    path: '/quantum-appeal/defence',
    element: createProtectedRoute(<AppealDefenceCompanion />, {
      tier: 'premium_pro',
      meta: {
        title: 'AppealOS · Defence Companion',
        toolId: 'quantum-appeal',
        category: 'integration',
      },
    }),
  },
  {
    id: 'quantum.appeal_core',
    domain: 'quantum',
    path: '/appeal-quantum-core/:id',
    element: <Navigate to="/quantum-appeal" replace />,
  },
  {
    id: 'quantum.leap_super',
    domain: 'quantum',
    path: '/quantum-leap-super',
    element: createProtectedRoute(<QuantumLeapSuperSystem />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Quantum Leap Super System',
        region: 'Multiverse',
        intent: 'engine',
        aiTags: ['quantum leap', 'super'],
        // Tool-usage tracking (see docs on RouteMeta.toolId). Listed in
        // NewZealandToolsEcosystem.tsx as id 'quantum-leap-super', but that
        // catalog entry is explicitly described as "Shared bulk-processing
        // tool (not NZ-specific)" — bare id, no country prefix, per the
        // cross-country/shared-tool convention.
        toolId: 'quantum-leap-super',
        category: 'integration',
      },
    }),
  },
  {
    id: 'quantum.leap_with_nav',
    domain: 'quantum',
    path: '/quantum-leap-nav',
    element: createProtectedRoute(<QuantumLeapWithNav />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Quantum Leap with Navigation',
        region: 'Multiverse',
        intent: 'engine',
        aiTags: ['quantum leap', 'navigation'],
      },
    }),
  },
  {
    id: 'quantum.unification',
    domain: 'quantum',
    path: '/quantum-unification',
    element: createProtectedRoute(<QuantumUnificationEngine />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Quantum Unification Engine',
        region: 'Multiverse',
        intent: 'engine',
        aiTags: ['unification', 'engine'],
      },
    }),
  },
  {
    id: 'quantum.control_center',
    domain: 'quantum',
    path: '/quantum-control-center',
    element: createProtectedRoute(<QuantumControlCenter />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Quantum Control Center',
        region: 'Multiverse',
        intent: 'dashboard',
        aiTags: ['quantum', 'control', 'settings'],
      },
    }),
  },
  {
    id: 'quantum.embassy_pulse',
    domain: 'quantum',
    path: '/embassy-quantum-pulse',
    element: <Navigate to="/journey-compass" replace />,
  },

  // Interdimensional / Multiverse
  {
    id: 'quantum.interdimensional',
    domain: 'quantum',
    path: '/interdimensional-framework',
    element: createProtectedRoute(<InterdimensionalFramework />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Interdimensional Framework',
        region: 'Multiverse',
        intent: 'framework',
        aiTags: ['interdimensional', 'framework'],
      },
    }),
  },
  {
    id: 'quantum.parallel_universes',
    domain: 'quantum',
    path: '/parallel-universes',
    element: createProtectedRoute(<ParallelUniversePage />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Parallel Universe Simulator',
        region: 'Multiverse',
        intent: 'simulator',
        aiTags: ['parallel', 'universe', 'simulator'],
      },
    }),
  },
  {
    id: 'quantum.multiverse_dashboard',
    domain: 'quantum',
    path: '/multiverse-dashboard',
    element: createProtectedRoute(<MultiverseDashboardPage />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Multiverse Dashboard',
        region: 'Multiverse',
        intent: 'dashboard',
        aiTags: ['multiverse', 'dashboard'],
      },
    }),
  },
  {
    id: 'quantum.reality_branches',
    domain: 'quantum',
    path: '/uk-visa-simulator/application/:id/branches',
    element: createProtectedRoute(<RealityBranchExplorer />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Reality Branch Explorer',
        region: 'Multiverse',
        intent: 'explorer',
        aiTags: ['reality', 'branches', 'explorer'],
      },
    }),
  },

  // Cosmic & Temporal
  {
    id: 'quantum.cosmic_consciousness',
    domain: 'quantum',
    path: '/cosmic-consciousness',
    element: createProtectedRoute(<CosmicImmigrationConsciousness />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Cosmic Immigration Consciousness',
        region: 'Multiverse',
        intent: 'consciousness',
        aiTags: ['cosmic', 'consciousness'],
      },
    }),
  },
  {
    id: 'quantum.temporal_paradox',
    domain: 'quantum',
    path: '/temporal-paradox',
    element: createProtectedRoute(<TemporalParadoxResolutionEngine />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Temporal Paradox Resolution Engine',
        region: 'Multiverse',
        intent: 'engine',
        aiTags: ['temporal', 'paradox'],
      },
    }),
  },
  {
    id: 'quantum.omega_convergence',
    domain: 'quantum',
    path: '/omega-convergence',
    element: createProtectedRoute(<OmegaConvergenceEngine />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Omega Convergence Engine',
        region: 'Multiverse',
        intent: 'engine',
        aiTags: ['omega', 'convergence'],
      },
    }),
  },

  // Quantum intelligence & predictions
  {
    id: 'quantum.predictions',
    domain: 'quantum',
    path: '/quantum-predictions',
    element: createProtectedRoute(<QuantumPredictions />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Quantum Predictions',
        region: 'Multiverse',
        intent: 'prediction',
        aiTags: ['quantum', 'predictions', 'ai'],
      },
    }),
  },
  {
    id: 'quantum.dependency_commander',
    domain: 'quantum',
    path: '/dependency-optimizer',
    element: createProtectedRoute(<QuantumDependencyCommander />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Quantum Dependency Commander',
        region: 'Multiverse',
        intent: 'tool',
        aiTags: ['dependency', 'commander'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — every
        // *ToolsEcosystem.tsx catalog (AU/DE/NZ/CA) lists this same path
        // under the consistent bare id 'dependency-optimizer', explicitly
        // not country-specific.
        toolId: 'dependency-optimizer',
        category: 'integration',
      },
    }),
  },
  {
    // Archived 2026-08-27: QuantumFlightIntelligenceHub retired in favour of
    // /flightos (FlightOS.tsx) — consolidated single flight cockpit per user
    // request. Old bookmarks/links redirect rather than 404.
    id: 'quantum.flight_hub',
    domain: 'quantum',
    path: '/flights',
    element: <Navigate to="/flightos" replace />,
  },
  {
    // Archived 2026-08-27 along with the rest of the Quantum flight-booking
    // ecosystem (src/flights/, src/pages/flights/) — none of it was wired
    // into /flightos. Redirects rather than 404s.
    id: 'quantum.flight_search',
    domain: 'quantum',
    path: '/flights/quantum-search',
    element: <Navigate to="/flightos" replace />,
  },
  {
    id: 'quantum.flight_suggestions',
    domain: 'quantum',
    path: '/flights/quantum-suggestions',
    element: <Navigate to="/flightos" replace />,
  },

  // Advanced systems
  {
    id: 'quantum.orchestrator',
    domain: 'quantum',
    path: '/quantum-orchestrator',
    element: createProtectedRoute(<QuantumOrchestrator />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Quantum Orchestrator',
        region: 'Multiverse',
        intent: 'orchestrator',
        aiTags: ['orchestrator', 'quantum'],
      },
    }),
  },
  {
    id: 'quantum.synergy_matrix',
    domain: 'quantum',
    path: '/tool-synergy-matrix',
    element: createProtectedRoute(<ToolSynergyMatrix />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Tool Synergy Matrix',
        region: 'Multiverse',
        intent: 'matrix',
        aiTags: ['synergy', 'matrix'],
        // Tool-usage tracking (see docs on RouteMeta.toolId). Listed in
        // NewZealandToolsEcosystem.tsx as id 'tool-synergy-matrix', but that
        // catalog entry is explicitly described as "Shared cross-tool
        // workflow view (not NZ-specific)" — bare id, no country prefix, per
        // the cross-country/shared-tool convention.
        toolId: 'tool-synergy-matrix',
        category: 'integration',
      },
    }),
  },
  {
    id: 'quantum.autonomous_workflows',
    domain: 'quantum',
    path: '/autonomous-workflows',
    element: createProtectedRoute(<AutonomousWorkflowOrchestrator />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Autonomous Workflow Orchestrator',
        region: 'Multiverse',
        intent: 'orchestrator',
        aiTags: ['autonomous', 'workflow'],
      },
    }),
  },
  {
    id: 'quantum.doc_intel_bridge',
    domain: 'quantum',
    path: '/document-intelligence-bridge',
    element: <Navigate to="/journey-compass" replace />,
  },
  {
    id: 'quantum.success_pattern_ai',
    domain: 'quantum',
    path: '/success-pattern-ai',
    element: createProtectedRoute(<SuccessPatternAI />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Success Pattern AI',
        region: 'Multiverse',
        intent: 'ai',
        aiTags: ['success', 'pattern', 'ai'],
      },
    }),
  },
  {
    id: 'quantum.emotional_intelligence',
    domain: 'quantum',
    path: '/emotional-intelligence',
    element: createProtectedRoute(<EmotionalIntelligenceEngine />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Emotional Intelligence Engine',
        region: 'Multiverse',
        intent: 'engine',
        aiTags: ['emotional', 'intelligence'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — cross-country
        // shared tool named explicitly in the tracking rollout scope, no
        // *ToolsEcosystem.tsx catalog entry to source a category from.
        toolId: 'emotional-intelligence',
      },
    }),
  },
  {
    id: 'quantum.predictive_optimizer',
    domain: 'quantum',
    path: '/predictive-optimizer',
    element: createProtectedRoute(<PredictiveOptimizer />, {
      tier: 'premium_pro',
      multiverseAccess: true,
      meta: {
        title: 'Predictive Optimizer',
        region: 'Multiverse',
        intent: 'optimizer',
        aiTags: ['predictive', 'optimizer'],
      },
    }),
  },
];